import cloneDeep from 'lodash/cloneDeep.js';
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import merge from 'lodash/merge.js';
import { DialogService } from '@/services/DialogService.js';
import BaseRubric from '@/store/modules/base/BaseRubric.js';
import Collection from '@/store/quill/Collection.js';

export default function ({ name }) {
    return merge(Collection(name, BaseRubric), {
        state: {},

        getters: {
            getRubric:
                state =>
                ({ target }) => {
                    return state[target];
                },

            getCriteriaField:
                state =>
                ({ target, order, field }) => {
                    return state[target].criterias[order][field];
                },

            getRatingField:
                state =>
                ({ target, parent, order, field }) => {
                    return state[target].criterias[parent].criteria_ratings[order][field];
                },
            getMaxValue:
                state =>
                ({ target, order }) => {
                    return state[target].criterias[order].max_value;
                },
        },

        actions: {
            async addRubric(context, payload) {
                const activity = context.rootGetters[`${payload.parentNamespace}/get`](payload.activity_uuid);

                const course_id = context.rootGetters['CourseTemplateForm/courseItem']('id');

                const rubric_uuid = activity.uuid;

                const template = activity.type.includes('temp');

                await context.dispatch('add', {
                    [template ? 'activity_template_id' : 'activity_id']: activity.id,
                    [template ? 'course_template_id' : 'course_id']: course_id,
                    criterias: [],
                    uuid: rubric_uuid,
                });

                await context.dispatch(
                    `${payload.parentNamespace}/updateItem`,
                    {
                        uuid: context.rootGetters[`${payload.parentNamespace}/getByKey`]({
                            keyName: 'id',
                            key: activity.id,
                            field: 'uuid',
                        }),
                        field: 'rubric_uuid',
                        value: rubric_uuid,
                    },
                    { root: true }
                );

                await context.dispatch(
                    `GradingScheme${template ? 'Template' : ''}s/add`,
                    {
                        uuid: rubric_uuid,
                        activity_id: activity.id,
                        activity_uuid: activity.uuid,
                    },
                    { root: true }
                );

                return rubric_uuid;
            },

            hydrateRubrics(context, { payload, rubric_type }) {
                if (!payload[rubric_type]) {
                    return false;
                }

                payload[rubric_type].criterias.sort((a, b) => (a.order > b.order ? 1 : -1));

                const rubric = cloneDeep(payload[rubric_type]);

                // Ensure all values are casted as numbers

                rubric.criterias.forEach(criteria => {
                    criteria.max_value = Number(criteria.max_value);

                    criteria.criteria_ratings.forEach(rating => {
                        rating.value = Number(rating.value);
                    });
                });

                payload.displayRubric = true;

                rubric.uuid = payload.uuid;

                if (!isEmpty(rubric.criterias)) {
                    rubric.criterias.forEach(criteria => {
                        if (!has(criteria, 'criteria_ratings')) {
                            criteria.criteria_ratings = [];
                        }
                    });
                }

                payload.rubric_uuid = rubric.uuid;

                // Rubrics/

                return context.dispatch('add', rubric);
            },

            reorder(context, { target, array }) {
                context.commit('REORDER', {
                    target,
                    array,
                    field: 'reorder',
                });
            },

            delete(context, payload) {
                return new Promise(resolve => {
                    const type = name.includes('Temp') ? 'rubric_template' : 'rubric';

                    if (!payload.field.includes('rating')) {
                        const rubric = context.getters.getRubric({
                            target: payload.target,
                        });

                        if (rubric.criterias.length === 1 && name !== 'RubricTemplates') {
                            context.dispatch('deleteRubric', { rubric, type });
                            return;
                        }

                        context.commit('REMOVE_CRITERIA', payload);

                        context.commit('REINDEX', payload);
                    } else {
                        context.commit('REMOVE_RATING', payload);
                    }

                    resolve();
                });
            },

            deleteRubric({ commit, rootGetters }, { rubric, type, message, has_submissions = false }) {
                const template = name.toLowerCase().includes('temp');

                const grading_scheme = rootGetters['GradingSchemes/get'](rubric.uuid);
                const grading_scheme_namespace = template ? 'GradingSchemeTemplates' : 'GradingSchemes';

                const activity_namespace = template ? 'ActivityTemplates' : 'Activities';

                const activity = rootGetters[`${activity_namespace}/getByKey`]({
                    key: Number(rubric[`activity${template ? '_template' : ''}_id`]),
                    keyName: 'id',
                });

                DialogService.confirm({
                    message:
                        message ||
                        '<p>Are you sure you want to <strong>remove</strong> this criteria?</p> </br> <p>Doing so will remove the <strong>entire</strong> rubric</p>',
                    title: 'Remove Rubric',
                    type: 'is-danger',
                    hasIcon: false,
                    confirmText: 'Remove',
                    cancelText: 'Go Back',
                    canCancel: ['button'],
                    onConfirm: async () => {
                        activity.rubric = null;

                        await commit('RESET', rubric.uuid);

                        commit(
                            `${activity_namespace}/${rubric.uuid}/EDIT_FIELD`,
                            {
                                field: type,
                                value: null,
                                has_submissions,
                            },
                            {
                                root: true,
                            }
                        );

                        if (grading_scheme && !isEmpty(grading_scheme.criterias)) {
                            commit(
                                `${grading_scheme_namespace}/EDIT_FIELD`,
                                {
                                    uuid: rubric.uuid,
                                    field: 'criterias',
                                    value: [],
                                },
                                { root: true }
                            );

                            commit(
                                `${grading_scheme_namespace}/REORDER`,
                                {
                                    uuid: rubric.uuid,
                                    field: 'reorder',
                                    type: 'activity',
                                },
                                { root: true }
                            );
                        }

                        if (rubric.id) {
                            const payload = cloneDeep(rubric);

                            commit('DELETE', {
                                field: 'delete',
                                rubric: payload,
                                has_submissions,
                            });

                            commit('EDIT', {
                                target: rubric.uuid,
                                field: 'id',
                                value: null,
                            });
                        }
                    },
                });
            },

            addRating(context, payload) {
                // TODO: REFACTOR

                const ratings = cloneDeep(
                    context.getters.getCriteriaField({
                        target: payload.target,
                        field: 'criteria_ratings',
                        order: payload.parent,
                    })
                );

                ratings.push(payload.value);

                context.commit('PUSH_RATING', {
                    target: payload.target,
                    parent: payload.parent,
                    field: payload.field,
                    value: payload.value,
                });

                return context.dispatch('orderRatings', {
                    ...payload,
                    order: payload.parent,
                    ratings,
                });
            },

            orderRatings(context, payload) {
                if (!has(payload, 'ratings')) {
                    payload.ratings = cloneDeep(
                        context.getters.getCriteriaField({
                            target: payload.target,
                            field: 'criteria_ratings',
                            order: payload.order,
                        })
                    );
                }

                if (payload.ratings.length > 1) {
                    payload.ratings.sort((x, y) => Number(x.value) - Number(y.value));

                    context.commit('SET_CRITERIA', {
                        target: payload.target,
                        order: payload.order,
                        field: 'criteria_ratings',
                        value: payload.ratings,
                    });
                }
            },
        },

        mutations: {
            EDIT(state, item) {
                state[item.target][item.field] = item.value;
            },

            PUSH(state, item) {
                state[item.target][item.field].push(item.value);
            },

            PUSH_RATING(state, item) {
                state[item.target].criterias[item.parent][item.field].push(item.value);
            },

            SET_CRITERIA(state, item) {
                state[item.target].criterias[item.order][item.field] = item.value;
            },

            SET_RATING(state, item) {
                state[item.target].criterias[item.parent].criteria_ratings[item.order][item.field] = item.value;
            },

            STAGE_RATING(state, item) {
                state[item.target].criterias[item.parent].criteria_ratings[item.order][item.field] = item.value;
            },

            REMOVE_CRITERIA(state, item) {
                delete state[item.target][item.field][item.key];
                const criterias = state[item.target][item.field];
                state[item.target][item.field] = criterias.filter(item => item !== null);
            },

            REMOVE_RATING(state, item) {
                delete state[item.target].criterias[item.parent][item.field][item.key];
                const rating = state[item.target].criterias[item.parent][item.field];
                state[item.target].criterias[item.parent][item.field] = rating.filter(item => item !== null);
            },

            REORDER(state, payload) {
                payload.array.forEach((item, index) => {
                    item.order = index;
                });

                state[payload.target].criterias = payload.array;
            },

            REINDEX(state, payload) {
                const array = state[payload.target].criterias;

                array.forEach((item, index) => {
                    item.order = index;
                });
            },

            RESET(state, target) {
                state[target].title = '';
                state[target].criterias = [];
            },

            DELETE() {},

            DELETE_RUBRIC_CRITERIA_TEMPLATE(state, payload) {
                state[payload.rubric.id].criterias = [];
            },
        },
    });
}
